import { useState, useCallback, useEffect, useRef } from "react";
import {
  getDarkwebIntelligence,
  getDarkwebProgress,
} from "../../api/get/get-darkweb";
import {
  configureDarkwebProvider,
  darkwebScan,
  disconnectDarkwebProvider,
  updateDarkwebRemediation,
} from "../../api/post/post-darkweb";
import { useAuth } from "../../context/contextAuth";

const EMPTY_INTELLIGENCE = {
  scans: [],
  sources: [],
  overview: {
    monitored_targets: 0,
    total_assessments: 0,
    total_exposures: 0,
    requires_attention: 0,
    critical: 0,
    high: 0,
    possible_active_credentials: 0,
    privileged_identities: 0,
    unverified: 0,
    risk_score: 0,
    severity: "unknown",
    identity_score: null,
    save_score_impact: 0,
    open_investigations: 0,
    last_assessment: null,
    last_successful_assessment: null,
    scoreable_assessments: 0,
    inconclusive_assessments: 0,
    assessment_status: "inconclusive",
    has_assessment: false,
    has_history: false,
  },
  context: {
    cloud: {
      providers: [],
      connected_providers: 0,
      flagged_findings: 0,
    },
    network: {
      assessed_assets: 0,
      critical_vulnerabilities: 0,
      high_vulnerabilities: 0,
      total_vulnerabilities: 0,
    },
  },
  correlation: {
    status: "not_established",
    verified: false,
    confidence: "insufficient",
    title: "No verified attack path established",
    explanation:
      "A validated exposure and identity-to-asset evidence are required.",
    path: [],
    signals: [],
    missing_links: [],
  },
  privacy: {
    raw_secrets_exposed: false,
    ai_context_sanitized: true,
  },
};

const useDarkwebData = () => {
  const { userId } = useAuth();
  const progressTimerRef = useRef(null);
  const [ScanLoading, setScanLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [intelligence, setIntelligence] = useState(EMPTY_INTELLIGENCE);
  const [darkwebAssets, setDarkwebAssets] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedScanId, setSelectedScanId] = useState(null);
  const [scanProgress, setScanProgress] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");

  const stopProgressPolling = useCallback(() => {
    if (progressTimerRef.current) {
      window.clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    stopProgressPolling();
    setResponse(null);
    setScanLoading(false);
    setIntelligence(EMPTY_INTELLIGENCE);
    setDarkwebAssets([]);
    setQuery("");
    setError(null);
    setCurrentIndex(0);
    setSelectedScanId(null);
    setScanProgress(null);
  }, [stopProgressPolling]);

  const refreshIntelligence = useCallback(async (preferredScanId = null) => {
    if (!userId) return EMPTY_INTELLIGENCE;
    const data = await getDarkwebIntelligence(userId);
    const normalized = {
      ...EMPTY_INTELLIGENCE,
      ...data,
      overview: {
        ...EMPTY_INTELLIGENCE.overview,
        ...(data?.overview || {}),
      },
      context: {
        ...EMPTY_INTELLIGENCE.context,
        ...(data?.context || {}),
        cloud: {
          ...EMPTY_INTELLIGENCE.context.cloud,
          ...(data?.context?.cloud || {}),
        },
        network: {
          ...EMPTY_INTELLIGENCE.context.network,
          ...(data?.context?.network || {}),
        },
      },
      correlation: {
        ...EMPTY_INTELLIGENCE.correlation,
        ...(data?.correlation || {}),
      },
    };
    setIntelligence(normalized);
    setDarkwebAssets(normalized.scans || []);
    setSelectedScanId((current) => {
      if (
        preferredScanId &&
        normalized.scans?.some(
          (scan) => scan.scan_uuid === preferredScanId
        )
      ) {
        return preferredScanId;
      }
      if (
        current &&
        normalized.scans?.some((scan) => scan.scan_uuid === current)
      ) {
        return current;
      }
      return normalized.scans?.[0]?.scan_uuid || null;
    });
    return normalized;
  }, [userId]);

  const initData = useCallback(async () => {
    if (!userId) {
      resetState();
      return;
    }
    try {
      setError(null);
      await refreshIntelligence();
    } catch (err) {
      console.error("Error fetching dark-web intelligence:", err);
      setError(err.message || "Unable to load dark-web intelligence");
    }
  }, [refreshIntelligence, resetState, userId]);

  const fetchProgress = useCallback(async () => {
    if (!userId) return;
    try {
      const progress = await getDarkwebProgress(userId);
      if (!progress) return;
      setScanProgress(progress);
      if (progress.status === "completed") {
        stopProgressPolling();
        setScanLoading(false);
        setQuery((current) =>
          /incomplete|inconclusive/i.test(progress.message || "")
            ? current
            : ""
        );
        refreshIntelligence(progress.scanId).catch((err) => {
          setError(
            err.message || "The assessment finished, but results could not be refreshed"
          );
        });
      } else if (progress.status === "error") {
        stopProgressPolling();
        setScanLoading(false);
        setError(progress.message || "Dark-web assessment failed");
      }
    } catch {
      // The scan request remains authoritative if progress polling is interrupted.
    }
  }, [refreshIntelligence, stopProgressPolling, userId]);

  const startProgressPolling = useCallback(() => {
    stopProgressPolling();
    progressTimerRef.current = window.setInterval(fetchProgress, 900);
  }, [fetchProgress, stopProgressPolling]);

  useEffect(() => {
    let cancelled = false;
    if (!userId) return undefined;

    getDarkwebProgress(userId)
      .then((progress) => {
        if (cancelled || !progress) return;
        setScanProgress(progress);
        if (["queued", "running"].includes(progress.status)) {
          setScanLoading(true);
          startProgressPolling();
        } else if (progress.status === "completed") {
          setScanLoading(false);
          refreshIntelligence(progress.scanId).catch((err) => {
            setError(
              err.message ||
                "The assessment finished, but results could not be refreshed"
            );
          });
        } else {
          setScanLoading(false);
        }
      })
      .catch(() => {
        // Progress recovery is best-effort; assessment history remains usable.
      });

    return () => {
      cancelled = true;
      stopProgressPolling();
    };
  }, [
    refreshIntelligence,
    startProgressPolling,
    stopProgressPolling,
    userId,
  ]);

  const handleScan = async () => {
    if (!userId || !query.trim() || ScanLoading) return;
    let scanContinuesInBackground = false;
    setScanLoading(true);
    setError(null);
    setResponse(null);
    setScanProgress({
      status: "running",
      stage: "starting",
      progress: 3,
      message: "Preparing the authorized assessment",
    });

    try {
      const res = await darkwebScan(userId, query.trim());
      setResponse(res.data);
      if (["queued", "running"].includes(res.data?.status)) {
        scanContinuesInBackground = true;
        setScanProgress((current) => ({
          ...(current || {}),
          scanId: res.data.scanId,
          status: res.data.status,
          stage: res.data.status,
          progress: Math.max(3, Number(current?.progress || 0)),
          message: res.message || "Assessment queued",
        }));
        startProgressPolling();
        return;
      }

      await refreshIntelligence(res.data?.scanId);
      setQuery("");
      setScanProgress((current) => ({
        ...(current || {}),
        status: "completed",
        stage: "completed",
        progress: 100,
        message: res.data?.summary?.score_available === false
          ? "Assessment finished with incomplete source coverage; no score was produced"
          : "Dark-web intelligence assessment completed",
      }));
    } catch (err) {
      setError(err.message || "Dark-web assessment failed");
      setScanProgress((current) => ({
        ...(current || {}),
        status: "error",
        stage: "error",
        progress: 100,
        message: err.message || "Dark-web assessment failed",
      }));
    } finally {
      if (!scanContinuesInBackground) {
        stopProgressPolling();
        setScanLoading(false);
      }
    }
  };

  const handleRemediationUpdate = useCallback(
    async (scanUuid, update) => {
      if (!userId || !scanUuid) return null;
      setError(null);
      try {
        const updated = await updateDarkwebRemediation(
          userId,
          scanUuid,
          update
        );
        await refreshIntelligence();
        return updated;
      } catch (err) {
        setError(err.message || "Unable to update remediation");
        throw err;
      }
    },
    [refreshIntelligence, userId]
  );

  const handleConfigureProvider = useCallback(
    async (providerKey, credentials) => {
      if (!userId) return null;
      setError(null);
      const result = await configureDarkwebProvider(
        userId,
        providerKey,
        credentials
      );
      await refreshIntelligence();
      return result;
    },
    [refreshIntelligence, userId]
  );

  const handleDisconnectProvider = useCallback(
    async (providerKey) => {
      if (!userId) return null;
      setError(null);
      const result = await disconnectDarkwebProvider(userId, providerKey);
      await refreshIntelligence();
      return result;
    },
    [refreshIntelligence, userId]
  );

  useEffect(
    () => () => {
      stopProgressPolling();
    },
    [stopProgressPolling]
  );

  return {
    ScanLoading,
    currentIndex,
    setCurrentIndex,
    darkwebAssets,
    intelligence,
    response,
    setResponse,
    error,
    query,
    setQuery,
    selectedScanId,
    setSelectedScanId,
    scanProgress,
    initData,
    refreshIntelligence,
    handleScan,
    handleConfigureProvider,
    handleDisconnectProvider,
    handleRemediationUpdate,
    resetState,
  };
};

export default useDarkwebData;
